import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/storybook-components/dist/Box'
import { Callout } from '@node-space/storybook-components/dist/Callout'
import CopyButton from '@node-space/storybook-components/dist/CopyButton'
import { DoubleLabelSelect } from '@node-space/storybook-components/dist/DoubleLabelSelect'
import Modal, {
  AlignActions,
  ModalActions,
  ModalNavBar,
  ModalScrollable,
} from '@node-space/storybook-components/dist/Modal'
import { Button, Icon } from '@node-space/ui-components'
import CopyRow from 'components/CopyRow'
import { useGetCustomersWalletsQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { CustomerDepositDetails } from 'pages/Customers/types'
import { AccountNumberTypes, CustomerWallet, LedgerWalletType } from 'types/customers'
import { formatString, roundNumberWithCommas } from 'utils/utils'
import { Currencies } from 'types/types'

interface CustomerDepositFormProps {
  customerDepositDetails: CustomerDepositDetails
  initialCustomerWallet: CustomerWallet
  isVisable: boolean
  onModalClose: () => void
}

const CustomerDepositForm = ({
  customerDepositDetails,
  initialCustomerWallet,
  isVisable,
  onModalClose,
}: CustomerDepositFormProps) => {
  const { t } = useTranslation()
  const [selectedCustomerWallet, setSelectedCustomerWallet] =
    useState<CustomerWallet>(initialCustomerWallet)
  const [copiedValue, setCopiedValue] = useState('')
  const [copiedTitle, setCopiedTitle] = useState('')

  const { data: { content: customerWallets } = {}, isFetching: isFetchingCustomerWallets } =
    useGetCustomersWalletsQuery({
      customerReference: initialCustomerWallet?.customerReference,
      size: 500,
    })

  useEffect(() => {
    setSelectedCustomerWallet(initialCustomerWallet)
  }, [initialCustomerWallet])

  const customerWalletOptions = customerWallets?.map(customerWallet => {
    return {
      icon: customerWallet?.balance?.currencyCode?.toLowerCase(),
      value: customerWallet?.id,
      label: customerWallet?.name,
      secondLabel: formatString(
        t('balanceWithCurrency'),
        roundNumberWithCommas(customerWallet?.balance?.value),
        customerWallet?.balance?.currencyCode
      ),
    }
  })

  const handleCustomerWalletOnChange = (customerWalletId: string) => {
    const selectedWallet = customerWallets?.find(
      customerWallet => customerWallet?.id === customerWalletId
    )

    setSelectedCustomerWallet(selectedWallet)
  }

  const depositDetails = useMemo(() => {
    if (customerDepositDetails && selectedCustomerWallet) {
      const fiatLedger = selectedCustomerWallet?.ledgers?.find(ledger => {
        if (selectedCustomerWallet?.balance?.currencyCode === Currencies.GBP) {
          return (
            ledger?.type === LedgerWalletType.FIAT &&
            ledger?.accountNumberFormat === AccountNumberTypes.SCAN
          )
        }
    
        return ledger?.type === LedgerWalletType.FIAT
      })

      if (fiatLedger) {
        return {
          ...(customerDepositDetails?.name && {
            [t('accountHolderName')]: customerDepositDetails?.name,
          }),
          ...(fiatLedger?.accountNumber && { [t('accountNumber')]: fiatLedger?.accountNumber }),
          ...(fiatLedger?.code && { [t('bankCode')]: fiatLedger?.code }),
          ...(customerDepositDetails?.address && {
            [t('beneficiaryAddress')]: customerDepositDetails?.address,
          }),
        }
      }
    }
  }, [customerDepositDetails, selectedCustomerWallet])

  const isDepositDetailsAvaialable =
    customerDepositDetails && selectedCustomerWallet && !!Object?.keys(depositDetails || {})?.length

  const Details = useMemo(() => {
    return Object.keys(depositDetails || {})
      .filter(label => !!label)
      .map(label => {
        return (
          <Box key={label}>
            <CopyRow
              key={label}
              title={label}
              description={depositDetails[label]}
              copiedValue={copiedValue}
              setCopiedValue={setCopiedValue}
              setCopiedTitle={setCopiedTitle}
            />
          </Box>
        )
      })
  }, [depositDetails])

  return (
    <Modal visible={isVisable} closeModal={onModalClose}>
      <Box data-testid="depositForm">
        <ModalNavBar title={t('deposit')} onClose={onModalClose} />
        <ModalScrollable>
          <Box paddingX={24} paddingT={24} paddingB={16}>
            <DoubleLabelSelect
              isSearchable
              loading={isFetchingCustomerWallets}
              iconsUrl={process.env.ICONS_URL}
              name={t('selectWallet')}
              label={t('selectWallet')}
              showOverflow
              menuMaxHeight={250}
              options={customerWalletOptions}
              value={selectedCustomerWallet?.id}
              onChange={(value: string) => handleCustomerWalletOnChange(value)}
            />

            {isDepositDetailsAvaialable && (
              <Box>
                <Box flex direction="col" paddingX={24} paddingY={16}>
                  <Box testid="beneficiaryDepositDetails" divideChildren="y">
                    {Details}
                  </Box>
                </Box>
                <Box flex justifyContent="center">
                  <CopyButton confirmationMessageTheme="dark" copyObject={depositDetails}>
                    <Button variant="ghost" iconPosition="right" iconElement={<Icon name="Copy" />}>
                      {t('copyAllDetails')}
                    </Button>
                  </CopyButton>
                </Box>
              </Box>
            )}
          </Box>
          {!isDepositDetailsAvaialable && (
            <Box paddingB={16} paddingX={20}>
              <Callout state="error" message={t('oopsSomethingWentWrongFetchDespoitDetails')} />
            </Box>
          )}
        </ModalScrollable>

        <ModalActions
          actions={[{ children: t('done'), onClick: onModalClose }]}
          alignActions={AlignActions.CENTER}
        />
      </Box>
    </Modal>
  )
}

export default CustomerDepositForm
